/**
 * fcmUtils.js
 * Firebase Cloud Messaging — client-side token registration + foreground handler.
 *
 * SETUP: Generate a VAPID key pair in Firebase Console → Project Settings →
 * Cloud Messaging → Web Push Certificates → Generate key pair.
 * Paste the public key into .env as REACT_APP_VAPID_KEY.
 */
import { getApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

const VAPID_KEY    = process.env.REACT_APP_VAPID_KEY;
const FCM_SW_PATH  = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope/';

let _messaging = null;
let _swReg     = null;
let _db        = null;

const getMsg = () => {
  if (!_messaging) { try { _messaging = getMessaging(getApp()); } catch (_) {} }
  return _messaging;
};
const getDB = () => {
  if (!_db) { try { _db = getFirestore(getApp()); } catch (_) {} }
  return _db;
};

// Register the dedicated FCM service worker at its own scope so it does not
// compete with sw.js (the caching service worker at scope /).
async function getFCMSwReg() {
  if (_swReg) return _swReg;
  try {
    _swReg = await navigator.serviceWorker.register(FCM_SW_PATH, { scope: FCM_SW_SCOPE });
  } catch (err) {
    console.warn('[FCM] SW registration failed:', err.message);
  }
  return _swReg;
}

/**
 * Request browser push permission and save the FCM token to Firestore.
 * Call after the user's role is confirmed (they are logged in and active).
 *
 * @param {string} uid  Firebase Auth UID of the current user
 * @returns {string|null} FCM token on success, null if denied / unsupported
 */
export async function requestFCMPermission(uid) {
  if (!uid)                                      return null;
  if (!('Notification' in window))               return null;
  if (!('serviceWorker' in navigator))           return null;
  if (Notification.permission === 'denied')      return null;

  if (!VAPID_KEY) {
    console.warn('[FCM] REACT_APP_VAPID_KEY not configured — skipping push registration');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = getMsg();
    if (!messaging) return null;

    const swReg = await getFCMSwReg();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return null;

    const db = getDB();
    if (db) {
      await updateDoc(doc(db, 'authorized_users', uid), { fcmToken: token });
    }
    return token;
  } catch (err) {
    // Errors here are non-fatal — the app still works without push
    console.warn('[FCM] Token registration failed:', err.message);
    return null;
  }
}

/**
 * Register a handler for messages received while the app tab is in the foreground.
 * Useful for showing in-app toast notifications instead of system notifications.
 *
 * @param {function} callback  Called with (payload) on each foreground message
 * @returns {function} Unsubscribe function — call on component unmount
 */
export function onForegroundMessage(callback) {
  const messaging = getMsg();
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}

/**
 * Capacitor Android: request native push permission and save the device token.
 * This is a no-op on web and when the Capacitor plugin is not installed.
 *
 * @param {string} uid Firebase Auth UID of the current user
 */
export async function registerAndroidPushToken(uid) {
  if (!uid) return;
  try {
    // Dynamic imports so the app doesn't crash if the Capacitor plugin is absent
    const { Capacitor }         = await import('@capacitor/core');
    const { PushNotifications } = await import('@capacitor/push-notifications');
    if (!Capacitor.isNativePlatform()) return;

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return;

    await PushNotifications.register();

    PushNotifications.addListener('registration', async ({ value: token }) => {
      const db = getDB();
      if (db && token) {
        await updateDoc(doc(db, 'authorized_users', uid), { fcmTokenAndroid: token });
      }
    });

    PushNotifications.addListener('registrationError', err => {
      console.warn('[FCM Android] Registration error:', err);
    });

    // Tapping a push opens the notification's page (handled by NotifNavigationBridge).
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const path = notification?.data?.navTarget;
      if (path) window.dispatchEvent(new CustomEvent('smc:navigate', { detail: { path } }));
    });
  } catch (_) {
    // Capacitor not present (running on web) — silently skip
  }
}
